import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ProcessCreateBulkCustomer } from './services/process-create-bulk-customer.service';
import { CreateBulkCustomer } from './services/create-bulk-customer.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CreateBulkClaims } from './services/create-bulk-claims.service';
import { ProcessCreateBulkClaims } from './services/process-create-bulk-claims.service';
import { LanguageService } from 'src/app/shared/services/language.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-add-bulk',
  templateUrl: './add-bulk.component.html',
  styleUrls: ['./add-bulk.component.scss']
})
export class AddBulkComponent implements OnInit {
  type: string = '';
  file: File | null = null;
  message: any;
  uploadFlag: boolean = true;
  countFlag: boolean = false;
  completeFlag: boolean = false;
  errorMsg: string = '';
  reportName: string = '';
  validationResponse: any[] = [];
  displayedColumns: string[] = [];
  displayTableColumns: any = {};
  totalNumberOfRecords: number = 0;

  constructor(
    public dialog: MatDialog,
    private translateService: TranslateService,
    private processCreateBulkCustomer: ProcessCreateBulkCustomer,
    private createBulkCustomer: CreateBulkCustomer,
    private processCreateBulkClaims: ProcessCreateBulkClaims,
    private createBulkClaims: CreateBulkClaims,
    private route: ActivatedRoute,
    private router: Router,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.initLanguage();
    this.route.queryParams.subscribe((params) => {
      this.type = params['type'] || '';
      if (!this.type) {
        this.router.navigate(['/']); // Redirect if no valid type
      }
      this.setDisplayedColumns();
    });
  }

  initLanguage(): void {
    const savedLang = localStorage.getItem('language') || 'en';
    this.loadContent(savedLang);

    this.languageService.languageUpdated.subscribe((lang: string) => {
      this.loadContent(lang);
    });
  }

  loadContent(lang: string): void {
    this.translateService.use(lang);
    this.reportName = lang === 'ar' ? 'نتيجة التحقق' : 'Validation Result';

    const columnMappings = {
      customers: {
        en: {
          customerBusinessName: 'customerBusinessName',
          firstName: 'firstName',
          lastName: 'lastName',
          email: 'email',
          mobile: 'mobile',
          address: 'address',
          additionalInfo: 'additionalInfo',
          status: 'status',
          reason: 'reason'
        },
        ar: {
          customerBusinessName: 'اسم عمل العميل',
          firstName: 'الاسم الأول',
          lastName: 'الاسم الأخير',
          email: 'البريد الإلكتروني',
          mobile: 'الموبايل',
          address: 'العنوان',
          additionalInfo: 'معلومات إضافية',
          status: 'الحالة',
          reason: 'السبب'
        }
      },
      claims: {
        en: {
          claimName: 'claimName',
          dueDate: 'dueDate',
          expiryDate: 'expiryDate',
          claimNumber: 'claimNumber',
          customerBusinessName: 'customerBusinessName',
          claimDescription: 'claimDescription',
          afterPaymentMessage: 'afterPaymentMessage',
          itemName: 'itemName',
          itemPrice: 'itemPrice',
          itemQuantity: 'itemQuantity',
          status: 'status',
          reason: 'reason'
        },
        ar: {
          claimName: 'اسم المطالبة',
          dueDate: 'تاريخ الاستحقاق',
          expiryDate: 'تاريخ انتهاء الصلاحية',
          claimNumber: 'رقم المطالبة',
          customerBusinessName: 'اسم عمل العميل',
          claimDescription: 'وصف المطالبة',
          afterPaymentMessage: 'بعد رسالة الدفع',
          itemName: 'اسم العنصر',
          itemPrice: 'سعر العنصر',
          itemQuantity: 'كمية العنصر',
          status: 'الحالة',
          reason: 'السبب'
        }
      }
    };

    this.displayTableColumns =
      columnMappings[this.type as keyof typeof columnMappings]?.[lang] || {};
  }

  setDisplayedColumns(): void {
    const columnConfigs: any = {
      customers: [
        'customerBusinessName',
        'firstName',
        'lastName',
        'email',
        'mobile',
        'address',
        'additionalInfo',
        'status',
        'reason'
      ],
      claims: [
        'claimName',
        'dueDate',
        'expiryDate',
        'claimNumber',
        'customerBusinessName',
        'claimDescription',
        'afterPaymentMessage',
        'itemName',
        'itemPrice',
        'itemQuantity',
        'status',
        'reason'
      ]
    };
    this.displayedColumns = columnConfigs[this.type] || [];
  }

  fileChange(event: any): void {
    const fileList: FileList = event.target.files;
    this.file = fileList.length > 0 ? fileList[0] : null;
  }

  async validate(): Promise<void> {
    if (!this.file) {
      this.errorMsg = this.translateService.instant('Please_upload_file');
      return;
    }

    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!allowedTypes.includes(this.file.type)) {
      this.errorMsg = this.translateService.instant('An_error_occurred_while_uploading_file');
      return;
    }


    let fileToUpload = this.file;

    if (this.file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      try {
        const csvBlob = await this.convertXlsxToCsv(this.file);
        fileToUpload = new File([csvBlob], this.file.name.replace('.xlsx', '.csv'), { type: 'text/csv' });
      } catch (error) {
        this.errorMsg = this.translateService.instant('Failed_to_convert_XLSX_to_CSV');
        return;
      }
    }

    const formData = new FormData();
    formData.append('file', fileToUpload);
    this.errorMsg = '';


    try {
      const response = await this.uploadFile(formData);
      if (!response || response.statusCode !== 200) {
        throw new Error('Validation failed');
      }

      this.processValidationResponse(response.data);
    } catch (error) {
      this.errorMsg = this.translateService.instant(
        'An_error_occurred_while_uploading_file'
      );
    }
  }

  private async convertXlsxToCsv(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: any) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]);

          resolve(new Blob([csv], { type: 'text/csv' }));
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  async uploadFile(formData: FormData): Promise<any> {
    if (this.type === 'customers') {
      this.processCreateBulkCustomer.setParams(formData);
      return await this.processCreateBulkCustomer.make();
    } else if (this.type === 'claims') {
      this.processCreateBulkClaims.setParams(formData);
      return await this.processCreateBulkClaims.make();
    }
  }

  processValidationResponse(data: any): void {

    const acceptedRows = data.acceptedRows.map((row: any) => ({
      ...row,
      dueDate: this.formatDate(row.dueDate),
      expiryDate: this.formatDate(row.expiryDate),
      status: 'Accepted',
      reason: ''
    }));
    const rejectedRows = data.rejectedRows.map((item: any) => ({
      ...item.row,
      dueDate: this.formatDate(item.row.dueDate),
      expiryDate: this.formatDate(item.row.expiryDate),
      status: 'Rejected',
      reason: Array.isArray(item.reason?.data)
      ? item.reason.data.map((r: any) => r.message).join('; ')
      : item.reason || 'Unknown reason'
    }));

    this.validationResponse = [...acceptedRows, ...rejectedRows];
    this.totalNumberOfRecords = this.validationResponse.length;
    this.uploadFlag = false;
    this.countFlag = true;

    if (this.isAllRejected()) {
      this.errorMsg = this.translateService.instant(
        'All_rows_are_rejected_please_upload_again'
      );
    }
  }

  formatDate = (dateValue: any) => {
    if (!dateValue) return '';

    if (typeof dateValue === 'number') {
      return this.excelSerialToDate(dateValue);
    }

    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-GB');
    }

    return dateValue;
  };

  excelSerialToDate(serial: number): string {
    const date = new Date((Number(serial) - 25569) * 86400000);
    return date.toLocaleDateString('en-GB');
  }

  async finish(): Promise<void> {
    this.errorMsg = '';
    const acceptedRows = this.validationResponse.filter(
      (row) => row.status === 'Accepted'
    );

    if (acceptedRows.length === 0) {
      this.errorMsg = this.translateService.instant('No_accepted_rows_to_submit');
      return;
    }

    try {
      const response = await this.submitAcceptedRows(acceptedRows);
      if (!response || response.statusCode !== 200) {
        throw new Error('Submit failed');
      }
      this.countFlag = false;
      this.completeFlag = true;
    } catch (error) {
      this.errorMsg = this.translateService.instant(
        'An_error_occurred_while_submitting_data'
      );
    }
  }

  async submitAcceptedRows(acceptedRows: any[]): Promise<any> {
    if (this.type === 'customers') {
      this.createBulkCustomer.setParams({ acceptedRows });
      return await this.createBulkCustomer.make();
    } else if (this.type === 'claims') {
      this.createBulkClaims.setParams({ acceptedRows });
      return await this.createBulkClaims.make();
    }
  }

  isAllRejected(): boolean {
    return this.validationResponse.every((row) => row.status === 'Rejected');
  }

  backToUpload(): void {
    this.uploadFlag = true;
    this.countFlag = false;
    this.file = null;
    this.completeFlag = false;
    this.errorMsg = '';
  }
}
